import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { NgbAlertModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { ConfirmationType, TYPE_COPY, TYPE_DELETE } from '../../model/translated-notification-messages';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { ComponentsModule } from '../components.module';
import { LookupOptions } from '../../core/util/lookup-options';

interface GenericTableRow {
  [key: string]: unknown;
  id?: string;
}

@Component({
  selector: 'app-table-actions',
  standalone: true,
  templateUrl: './table-actions.component.html',
  imports: [CommonModule, NgbAlertModule, NgbTooltipModule, ComponentsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  styleUrl: './table-actions.component.scss'
})
export class TableActionsComponent implements ICellRendererAngularComp {
  // Properties that are set from context
  deleteSending$?: Observable<boolean>;
  isLookup = false;
  lookupValues: string[] = [];
  lookupOptions?: LookupOptions;
  copyForbidden = false;
  showHistoryButton = false;

  searchResult: GenericTableRow = {};
  recordIndex = '';
  rowIndex = 0;
  confirmationType: ConfirmationType = undefined;
  params!: ICellRendererParams;
  rowName = '';
  isDeleting = false;
  getConfirmation = false;

  readonly TYPE_COPY = TYPE_COPY;
  readonly TYPE_DELETE = TYPE_DELETE;

  agInit(params: ICellRendererParams): void {
    this.searchResult = params.data ?? {};
    this.recordIndex = params.data?.id ?? '';
    this.rowIndex = params.node?.rowIndex ?? 0;
    this.params = params;
    this.rowName = params.data?.name ?? '';
    this.rowIndex = params.node?.rowIndex ?? 0;

    // Get parent context
    const context = params.context;

    this.isLookup = context?.isLookup || false;
    this.lookupValues = context?.lookupValues || [];
    this.lookupOptions = context?.lookupOptions;
    this.deleteSending$ = context?.deleteSending$;
    this.showHistoryButton = params.colDef?.cellRendererParams?.showHistoryButton || false;
    this.copyForbidden = params.colDef?.cellRendererParams?.copyForbidden || false;
    const activeConfirmationRowId$: BehaviorSubject<string | null> =
      params.context.componentParent.activeConfirmationRowId$;

    const deleteSendingObs: Observable<boolean> = this.deleteSending$ || new BehaviorSubject<boolean>(false);
    const activeConfirmationRowIdObs: Observable<string | null> =
      (activeConfirmationRowId$ && activeConfirmationRowId$.asObservable()) || new BehaviorSubject<string | null>(null);

    combineLatest([deleteSendingObs, activeConfirmationRowIdObs]).subscribe(([isSending, activeRowId]) => {
      // Check if the current row is the active confirmation row
      if (activeRowId === this.recordIndex) {
        this.isDeleting = isSending;
      } else {
        this.isDeleting = false;
        if (this.getConfirmation) {
          this.getConfirmation = false;
          this.confirmationType = undefined;
        }
      }
    });
  }

  refresh(_params: ICellRendererParams): boolean {
    this.params = _params;
    return true;
  }

  executeActionAfterConfirmation() {
    const parent = this.params?.context?.componentParent;
    const actionType = this.confirmationType;

    if (!parent || !this.params?.api) {
      return;
    }

    // Execute the action using generic handlers
    if (actionType === TYPE_DELETE && parent.deleteHandler && this.params.data) {
      this.isDeleting = true;
      parent.deleteHandler(this.params.data);
    } else if (actionType === TYPE_COPY && parent.copyHandler && this.params.data) {
      parent.copyHandler(this.params.data);
    }

    // Clear confirmation state after action
    this.goBackConfirmation();
  }

  goBackConfirmation() {
    // Clear local confirmation state
    this.confirmationType = undefined;
    this.getConfirmation = false;

    // Clear parent confirmation state
    const parent = this.params?.context?.componentParent;
    if (parent?.setActiveConfirmationRowId) {
      parent.setActiveConfirmationRowId(null);
      this.isDeleting = false;
    }
  }

  getConfirmationMessage() {
    if (this.confirmationType === TYPE_DELETE) {
      return $localize`:@@upp.global.messages.delete:Delete`;
    } else if (this.confirmationType === TYPE_COPY) {
      return $localize`:@@upp.global.messages.copy:Copy`;
    } else {
      return '';
    }
  }

  getConfirmationActionMessage() {
    if (this.confirmationType === TYPE_DELETE) {
      return $localize`:@@upp.global.button.delete:Delete`;
    } else if (this.confirmationType === TYPE_COPY) {
      return $localize`:@@upp.global.search.button.copy:Copy`;
    } else {
      return '';
    }
  }

  askForConfirmation(type: ConfirmationType) {
    if (!this.params || !this.params.api) {
      return;
    }

    // Access the parent component via the context
    const parent = this.params.context.componentParent;
    // Notify the parent about the new active row
    parent.setActiveConfirmationRowId(this.recordIndex);

    // Update confirmation state
    this.getConfirmation = true;
    this.confirmationType = type;
  }

  modifyDetails(detailId: string | undefined) {
    if (!detailId) {
      return;
    }
    const parent = this.params.context.componentParent;

    if (parent.modifyHandler) {
      parent.modifyHandler(this.params.data);
    }
  }

  showDetails(id: string | undefined) {
    if (!id) {
      return;
    }
    const parent = this.params.context.componentParent;

    if (parent.displayHandler) {
      parent.displayHandler(this.params.data);
    }
  }

  showHistory(id: string) {
    if (!id) {
      return;
    }
    const parent = this.params.context.componentParent;

    if (parent.showHistoryHandler) {
      parent.showHistoryHandler(this.params.data);
    }
  }
}
